import clsx from "clsx";
import Link from "next/link";
import { useRouter } from "next/router";

import { IconButton } from "@chef/components";
import { ChevronLeft, ChevronRight } from "@chef/icons/small";

import { intl } from "./Pagination.Intl";

interface StepperPaginationProps {
  page: number;
  total: number;
  className?: string;
}

interface NavButtonProps {
  href: string;
  disabled: boolean;
}

const createNextPageHref = (path: string, page: number) => {
  // Check if path already has a page number
  const pageMatch = path.match(/\/\d+$/);

  if (pageMatch) {
    return path.replace(/\/\d+$/, `/${page}`);
  }

  return `${path}/${page}`;
};

const createPreviousPageHref = (path: string, page: number) => {
  // Check if path already has a page number
  const pageMatch = path.match(/\/\d+$/);

  if (pageMatch) {
    return path.replace(/\/\d+$/, `/${page}`);
  }

  return `${path}/${page}`;
};

const PrevButton = ({ href, disabled }: NavButtonProps) => {
  if (disabled) {
    return (
      <IconButton disabled label={intl.PREVIOUS}>
        <ChevronLeft className="opacity-30" />
      </IconButton>
    );
  }

  return (
    <Link href={href}>
      <IconButton label={intl.PREVIOUS} variant="outlined">
        <ChevronLeft />
      </IconButton>
    </Link>
  );
};

const NextButton = ({ href, disabled }: NavButtonProps) => {
  if (disabled) {
    return (
      <IconButton label={intl.NEXT} disabled>
        <ChevronRight className="opacity-30" />
      </IconButton>
    );
  }

  return (
    <Link href={href}>
      <IconButton label={intl.NEXT} variant="outlined">
        <ChevronRight />
      </IconButton>
    </Link>
  );
};

export const StepperPagination = ({
  page,
  total,
  className,
}: StepperPaginationProps) => {
  const router = useRouter();

  const prevPage = Math.max(1, page - 1);
  const nextPage = Math.min(total, page + 1);

  const isPrevDisabled = page === 1;
  const isNextDisabled = page === total;

  const nextPath = createNextPageHref(router.asPath, nextPage);
  const prevPath = createPreviousPageHref(router.asPath, prevPage);

  if (page <= 0 || page > total) {
    return null;
  }

  return (
    <div
      className={clsx(className, "flex items-center justify-center gap-4 mt-8")}
    >
      <PrevButton href={prevPath} disabled={isPrevDisabled} />
      <span>
        {page} {intl.OF} {total}
      </span>
      <NextButton href={nextPath} disabled={isNextDisabled} />
    </div>
  );
};
